import { useState } from "react";
import { Calendar } from "./ui/calendar";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { toast } from "sonner";
import { Calendar as CalendarIcon, Clock, ArrowRight } from "lucide-react";
import { bookAppointment, getAvailableSlots } from "../services/appointmentService";
import { useTranslation } from "react-i18next";
import { useInView } from "../hooks/useInView";

const AppointmentBooking = () => {
  const { t } = useTranslation();
  const [sectionRef, inView] = useInView(0.1);
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedTime, setSelectedTime] = useState("");
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    customerName: "",
    email: "",
    phone: "",
    service: "",
    address: "",
    notes: ""
  });

  const handleDateSelect = async (date) => {
    setSelectedDate(date);
    setSelectedTime("");
    setIsLoadingSlots(true);
    
    try {
      const slots = await getAvailableSlots(date);
      setAvailableSlots(slots);
      if (slots.length === 0) {
        toast.info("No available slots for this date. Please select another day.");
      }
    } catch (error) {
      toast.error("Failed to load available slots");
    } finally {
      setIsLoadingSlots(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData(prev => ({ ...prev, service: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedDate || !selectedTime) {
      toast.error("Please select a date and time");
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const appointmentData = {
        ...formData,
        date: selectedDate.toISOString().split('T')[0],
        time: selectedTime
      };
      
      const result = await bookAppointment(appointmentData);
      
      if (result.success) {
        toast.success(result.message);
        toast.info(`Appointment ID: ${result.appointment.id}. Save this to reschedule later!`);
        
        // Reset form
        setFormData({
          customerName: "",
          email: "",
          phone: "",
          service: "",
          address: "",
          notes: ""
        });
        setSelectedDate(null);
        setSelectedTime("");
        setAvailableSlots([]);
      }
    } catch (error) {
      toast.error("Failed to book appointment. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const isPastDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  return (
    <section
      id="appointments"
      ref={sectionRef}
      className="relative py-20 bg-gradient-to-b from-white via-[#f0f9fb] to-white overflow-hidden"
    >
      <style>{`
        @keyframes apptSlideInLeft {
          from { opacity: 0; transform: translateX(-50px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes apptSlideInRight {
          from { opacity: 0; transform: translateX(50px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes apptBlobFloat {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50%      { transform: translate(20px, -30px) scale(1.08); }
        }
        @keyframes apptCalIconBounce {
          0%, 100% { transform: rotate(0deg) scale(1); }
          50%      { transform: rotate(-8deg) scale(1.15); }
        }
        @keyframes apptCtaShimmer {
          0%   { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        @keyframes apptCtaPulse {
          0%, 100% { box-shadow: 0 0 0 0   rgba(26, 122, 138, 0.55); }
          50%      { box-shadow: 0 0 0 16px rgba(26, 122, 138, 0); }
        }
        .appt-card-left  { opacity: 0; }
        .appt-card-right { opacity: 0; }
        .appt-card-left.is-in  { animation: apptSlideInLeft  0.8s cubic-bezier(0.22, 0.61, 0.36, 1) forwards; }
        .appt-card-right.is-in { animation: apptSlideInRight 0.8s 0.15s cubic-bezier(0.22, 0.61, 0.36, 1) forwards; }
        .appt-blob {
          position: absolute;
          border-radius: 50%;
          filter: blur(60px);
          opacity: 0.4;
          pointer-events: none;
          animation: apptBlobFloat 10s ease-in-out infinite;
        }
        .appt-cal-icon {
          animation: apptCalIconBounce 3s ease-in-out infinite;
          display: inline-block;
        }
        .appt-cta {
          position: relative;
          overflow: hidden;
          background: linear-gradient(
            90deg,
            #1a7a8a 0%,
            #3da5ba 25%,
            #5ec4d9 50%,
            #3da5ba 75%,
            #1a7a8a 100%
          );
          background-size: 200% 100%;
          animation:
            apptCtaShimmer 4s linear infinite,
            apptCtaPulse 2.6s ease-in-out infinite;
          transition: transform 0.3s cubic-bezier(0.22, 0.61, 0.36, 1);
        }
        .appt-cta:hover:not(:disabled) {
          transform: translateY(-2px) scale(1.02);
        }
        .appt-cta:disabled {
          background: #94a3b8;
          animation: none;
        }
        .appt-cta-arrow {
          transition: transform 0.3s ease;
        }
        .appt-cta:hover:not(:disabled) .appt-cta-arrow {
          transform: translateX(4px);
        }
      `}</style>

      {/* Decorative animated blobs */}
      <div className="appt-blob bg-[#3da5ba]/30 w-72 h-72 -top-20 -left-20" style={{ animationDelay: '0s' }} />
      <div className="appt-blob bg-[#1a7a8a]/25 w-96 h-96 -bottom-32 -right-20" style={{ animationDelay: '3s' }} />

      <div className="relative z-10 container mx-auto px-4 lg:px-8">
        <div className="text-center mb-16">
          <h2 className={`anim-heading text-4xl md:text-5xl font-bold text-gray-900 mb-4 ${inView ? 'is-in' : ''}`}>
            {t('appointments.title')}
          </h2>
          <div className="flex justify-center mb-4">
            <div className={`anim-heading-underline ${inView ? 'is-in' : ''}`} />
          </div>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {t('appointments.subtitle')}
          </p>
        </div>

        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Calendar Section */}
            <Card className={`appt-card-left ${inView ? 'is-in' : ''} shadow-xl border-0`}>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <span className="appt-cal-icon mr-2 text-[#1a7a8a]">
                    <CalendarIcon size={24} />
                  </span>
                  {t('appointments.selectDate')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="mb-6">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={handleDateSelect}
                    disabled={isPastDate}
                    className="rounded-md border mx-auto"
                  />
                </div>

                {selectedDate && (
                  <div>
                    <Label className="flex items-center mb-3">
                      <Clock className="mr-2 text-[#1a7a8a]" size={18} />
                      {t('appointments.availableSlots')}
                    </Label>
                    
                    {isLoadingSlots ? (
                      <div className="text-center py-4 text-gray-500">{t('appointments.loading')}</div>
                    ) : availableSlots.length > 0 ? (
                      <div className="grid grid-cols-3 gap-2">
                        {availableSlots.map((slot) => (
                          <Button
                            key={slot}
                            type="button"
                            variant={selectedTime === slot ? "default" : "outline"}
                            className={selectedTime === slot ? "bg-[#1a7a8a] hover:bg-[#156a78]" : ""}
                            onClick={() => setSelectedTime(slot)}
                          >
                            {slot}
                          </Button>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-4 text-gray-500">
                        {t('appointments.noSlots')}
                      </div>
                    )}
                  </div>
                )}

                {selectedDate && selectedTime && (
                  <div className="mt-4 p-4 bg-[#1a7a8a] 50 rounded-lg">
                    <p className="font-semibold text-gray-900">{t('appointments.selected')}</p>
                    <p className="text-gray-700">
                      {selectedDate.toLocaleDateString('en-US', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </p>
                    <p className="text-[#156a78] font-semibold">{selectedTime}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Form Section */}
            <Card className={`appt-card-right ${inView ? 'is-in' : ''} shadow-xl border-0`}>
              <CardHeader>
                <CardTitle>{t('appointments.details')}</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="customerName">{t('appointments.name')} *</Label>
                    <Input
                      id="customerName"
                      name="customerName"
                      value={formData.customerName}
                      onChange={handleChange}
                      required
                      placeholder="John Doe"
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email">{t('appointments.email')} *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="john@example.com"
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone">{t('appointments.phone')} *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="+31 6 1234 5678"
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="service">{t('appointments.service')} *</Label>
                    <Select value={formData.service} onValueChange={handleServiceChange} required>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="House Renovation">{t('service.houseReno.title')}</SelectItem>
                        <SelectItem value="Bathroom Renovation">{t('service.bathroom.title')}</SelectItem>
                        <SelectItem value="Painting">{t('service.painting.title')}</SelectItem>
                        <SelectItem value="Plumbing">{t('service.plumbing.title')}</SelectItem>
                        <SelectItem value="Plastering">{t('service.plastering.title')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="address">{t('appointments.address')} *</Label>
                    <Input
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      required
                      placeholder="Street address, Amsterdam"
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="notes">{t('appointments.notes')}</Label>
                    <Textarea
                      id="notes"
                      name="notes"
                      value={formData.notes}
                      onChange={handleChange}
                      placeholder="Any specific requirements or questions..."
                      rows={3}
                      className="mt-1"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting || !selectedDate || !selectedTime}
                    className="appt-cta w-full text-white text-base font-semibold py-6 group"
                  >
                    {isSubmitting ? t('appointments.booking') : t('appointments.book')}
                    {!isSubmitting && (
                      <ArrowRight className="appt-cta-arrow ml-2" size={20} />
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AppointmentBooking;
